<template>
  <Card variant="tertiary" size="medium">
    <View gap="sm">
      <Icon v-if="isShowingConfirmed" name="calendar-check-01" size="small" class="confirmed-icon" />

      <View direction="column" gap="sm" :grow="1">
        <View direction="column" gap="xs">
          <View justify="space-between" gap="sm">
            <Text variant="body-small" color="secondary">{{ title }}</Text>
            <Text variant="body-small" color="tertiary" :title="requestedAt">{{ timeAgo }}</Text>
          </View>

          <Text variant="h4" v-if="showingStartTime && showingEndTime">{{ dateWithTime }}</Text>
        </View>

        <View direction="row" gap="sm" align="center" wrap="wrap">
          <View :grow="1" gap="sm" align="center">
            <PropertyPhotoPreview :image-url="propertyPhoto" size="xs" />
            <Text variant="label-small">{{ propertyAddress }}</Text>
          </View>

          <Button v-if="isShowingConfirmed" :to="showingLink" size="small" variant="ghost">Reconsider</Button>

          <div class="actions" v-if="isShowingPending">
            <Button size="small" @click="openAcceptModal">Accept</Button>
            <Button size="small" variant="ghost" @click="openDeclineModal">Decline</Button>
            <Button size="small" variant="ghost" @click="openRescheduleModal">Reschedule</Button>
          </div>
        </View>
      </View>
    </View>

    <AcceptShowingRequestModal
      ref="acceptModalRef"
      :renter-name="userName"
      :property-id="propertyId"
      :showing-request-id="showingRequest.id"
      :showing-agent-id="showingAgentId"
      @accepted="emit('showingRequestStatusChanged')"
    />

    <DeclineShowingRequestModal
      ref="declineModalRef"
      :property-id="propertyId"
      :showing-request-id="showingRequest.id"
      @declined="emit('showingRequestStatusChanged')"
    />

    <RescheduleShowingRequestModal
      ref="rescheduleModalRef"
      :property-id="propertyId"
      :showing-request-id="showingRequest.id"
      :showing-agent-id="showingAgentId"
      :open-new-showing-after-reschedule="openNewShowingAfterReschedule"
      @rescheduled="emit('showingRequestStatusChanged')"
    />
  </Card>
</template>

<script setup lang="ts">
import { useDateFormat, useTimeAgo } from '@vueuse/core';
import { computed, ref } from 'vue';

import { Button, Card, Icon, Text, View } from '@tallo/design-system';
import { showingDetailsRoute } from '@tallo/investor/navigation';
import { PropertyPhotoPreview } from '@tallo/property';
import { useTimeAgoShortMessages } from '@tallo/utility';

import { ShowingRequestStatus } from '../../interfaces/showing-request-status.enum';
import { ShowingRequestDto } from '../../interfaces/showing-request.interface';
import { getShowingTime } from '../../utils/get-showing-time.util';
import AcceptShowingRequestModal from './accept-showing-request-modal.vue';
import DeclineShowingRequestModal from './decline-showing-request-modal.vue';
import RescheduleShowingRequestModal from './reschedule-showing-request-modal.vue';

interface ShowingRequestActionsProps {
  propertyId: string;
  propertyAddress: string;
  propertyPhoto?: string;
  showingRequest: ShowingRequestDto;
  showingId: string;
  showingAgentId?: string;
  showingRequestStatus: ShowingRequestStatus;
  showDate?: boolean;
  showingStartTime?: string | Date;
  showingEndTime?: string | Date;
  isRenterProfileClickable?: boolean;
  openNewShowingAfterReschedule?: boolean;
}

const props = withDefaults(defineProps<ShowingRequestActionsProps>(), {
  showDate: true,
  isRenterProfileClickable: true,
});

const emit = defineEmits<{ showingRequestStatusChanged: [] }>();

const acceptModalRef = ref<typeof AcceptShowingRequestModal | null>(null);
const declineModalRef = ref<typeof DeclineShowingRequestModal | null>(null);
const rescheduleModalRef = ref<typeof RescheduleShowingRequestModal | null>(null);
const userName = computed(() => props.showingRequest.renter.user.name);
const dateWithTime = computed(() => {
  if (props.showingStartTime && props.showingEndTime) {
    return getShowingTime(props.showingStartTime, props.showingEndTime);
  }

  return '';
});
const requestedAt = computed(() => useDateFormat(props.showingRequest.createdAt, 'MM/DD/YYYY hh:mm').value);
const timeAgo = computed(() => useTimeAgo(props.showingRequest.createdAt, { messages: useTimeAgoShortMessages }).value);
const showingLink = computed(() => showingDetailsRoute(props.showingId));
const isShowingConfirmed = computed(() => props.showingRequestStatus === ShowingRequestStatus.ACCEPTED);
const isShowingPending = computed(() => props.showingRequestStatus === ShowingRequestStatus.PENDING);
const title = computed(() => {
  switch (true) {
    case isShowingConfirmed.value:
      return `Showing scheduled with ${userName.value}`;

    case isShowingPending.value:
      return `${userName.value} requested for showing`;

    default:
      return '';
  }
});
function openAcceptModal() {
  acceptModalRef.value?.open();
}

function openDeclineModal() {
  declineModalRef.value?.open();
}

function openRescheduleModal() {
  rescheduleModalRef.value?.open();
}
</script>

<style scoped lang="scss">
.actions {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  gap: var(--t-spacing-sm);
}

.confirmed-icon {
  color: var(--t-color-green-500);
}
</style>
